/**
 * Page Preload Registry
 *
 * This file contains the configuration for all pages that need preloading.
 * Each entry defines how to check if that page "has items".
 *
 * IMPORTANT: When adding a new preload page, add an entry here.
 */

import api from 'modules/queries/api';
import { ASSET_LIST_QUERY_KEY } from 'modules/queries/asset/useList';
import { NETWORK_PRIVATE_GET_QUERY_KEY } from 'modules/queries/network/private/useGet';
import { ACCOUNT_NETWORK_PRIVATE_LIST_QUERY_KEY } from 'modules/queries/account/network/private/useList';
import { NETWORK_FIREWALL_RULESET_LIST_QUERY_KEY } from 'modules/queries/network/firewall/ruleset/useList';
import { STORAGE_BLOCK_VOLUME_LIST_QUERY_KEY } from 'modules/queries/storage/block/volume/useList';
import { SAVED_RULES_PRESET_NAME_PREFIX } from 'utility/firewall';
import { paths, type Path } from 'containers/LeftHandLayout/context';

export type PagePreloadKey =
	| 'cloudIPAddresses'
	| 'vpns'
	| 'cloudPrivateNetwork'
	| 'globalPrivateFabric'
	| 'firewallRulesets'
	| 'cloudBlockStorage'
	| 'cloudObjectStorage';

export interface PagePreloadConfig {
	key: PagePreloadKey;
	/** Type-safe path reference - must match a path from LeftHandLayout */
	path: Path;
	/** Query key used by React Query for caching */
	queryKey: readonly unknown[];
	/** Function to fetch and determine if the page has items */
	queryFn: () => Promise<{ hasItems: boolean }>;
}

/**
 * Registry of all pages that need preloading and their "has items" check.
 *
 * Each entry uses a minimal query (page_size: 1) to quickly determine
 * if there are any items, without fetching full data.
 */
export const pagePreloadRegistry: PagePreloadConfig[] = [
	{
		key: 'cloudIPAddresses',
		path: paths.CloudIPAddresses,
		queryKey: [
			ASSET_LIST_QUERY_KEY,
			{ type: ['SS.IP', 'SS.VIP'], page_size: 1 },
			'pagePreload',
		] as const,
		queryFn: async () => {
			// Check both IP Pools and Virtual IPs - page has items if either has items
			const [ipPoolsResult, virtualIPsResult] = await Promise.all([
				api.asset.apiRaw.list({ type: 'SS.IP', page_size: 1 }),
				api.asset.apiRaw.list({ type: 'SS.VIP', page_size: 1 }),
			]);
			return {
				hasItems:
					(ipPoolsResult.item_total ?? 0) > 0 ||
					(virtualIPsResult.item_total ?? 0) > 0,
			};
		},
	},
	{
		key: 'vpns',
		path: paths.vpns,
		queryKey: [
			ASSET_LIST_QUERY_KEY,
			{ type: 'SS.VPN', page_size: 1 },
			'pagePreload',
		] as const,
		queryFn: async () => {
			const result = await api.asset.apiRaw.list({
				type: 'SS.VPN',
				page_size: 1,
			});
			return { hasItems: (result.item_total ?? 0) > 0 };
		},
	},
	{
		key: 'cloudPrivateNetwork',
		path: paths.cloudPrivateNetwork,
		queryKey: [NETWORK_PRIVATE_GET_QUERY_KEY, {}, 'pagePreload'] as const,
		queryFn: async () => {
			const result = await api.network.private.apiRaw.get({});
			// Cloud Private Network has items if any zone has attached or unattached servers
			const hasItems = result.zones.some(
				(zone) =>
					zone.type === 'liquidweb' &&
					(zone.attached.length > 0 || zone.unattached.length > 0),
			);
			return { hasItems };
		},
	},
	{
		key: 'globalPrivateFabric',
		path: paths.globalPrivateFabric,
		queryKey: [
			ACCOUNT_NETWORK_PRIVATE_LIST_QUERY_KEY,
			{},
			'pagePreload',
		] as const,
		queryFn: async () => {
			const result = await api.account.network.private.apiRaw.list({});
			return { hasItems: (result.items?.length ?? 0) > 0 };
		},
	},
	{
		key: 'firewallRulesets',
		path: paths.firewallRulesets,
		queryKey: [
			NETWORK_FIREWALL_RULESET_LIST_QUERY_KEY,
			{ page_size: 1 },
			'pagePreload',
		] as const,
		queryFn: async () => {
			const [rulesetResult] = await Promise.all([
				api.network.firewall.ruleset.apiRaw.list({
					page_size: 1,
				}),
				// Preload asset list for the page
				api.asset.apiRaw.list({
					alsowith: ['altGroup', 'capabilities'],
				}),
			]);
			// Filter out saved presets - only count user-created rulesets
			const userRulesets =
				rulesetResult.items?.filter(
					(r) => !r.ruleset.startsWith(SAVED_RULES_PRESET_NAME_PREFIX),
				) ?? [];
			return { hasItems: userRulesets.length > 0 };
		},
	},
	{
		key: 'cloudBlockStorage',
		path: paths.cloudBlockStorage,
		queryKey: [
			STORAGE_BLOCK_VOLUME_LIST_QUERY_KEY,
			{ page_size: 1 },
			'pagePreload',
		] as const,
		queryFn: async () => {
			const result = await api.storage.block.volume.apiRaw.list({
				page_size: 1,
			});
			return { hasItems: (result.items?.length ?? 0) > 0 };
		},
	},
	{
		key: 'cloudObjectStorage',
		path: paths.objectStorage,
		queryKey: [
			ASSET_LIST_QUERY_KEY,
			{ category: ['ObjectStorage'], page_size: 1 },
			'pagePreload',
		] as const,
		queryFn: async () => {
			const result = await api.asset.apiRaw.list({
				category: ['ObjectStorage'],
				page_size: 1,
			});
			return { hasItems: (result.item_total ?? 0) > 0 };
		},
	},
];

/**
 * Get the configuration for a specific page preload
 */
export const getPagePreloadConfig = (
	key: PagePreloadKey,
): PagePreloadConfig | undefined => {
	return pagePreloadRegistry.find((config) => config.key === key);
};

/**
 * Get the preload key for a specific path.
 * Returns undefined if the path doesn't have a preload configuration.
 */
export const getPreloadKeyForPath = (
	path: Path,
): PagePreloadKey | undefined => {
	const config = pagePreloadRegistry.find((c) => c.path === path);
	return config?.key;
};
